import PropTypes from "prop-types";
import { useMemo } from "react";

import {
  FormSkeleton,
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import ContactForm from "routes/contact/form/ContactForm";
import { MODEL_TYPE } from "routes/contact/form/constants";
import { FORM_MODES } from "routes/contact/form/defaults";

/**
 * Creates a Contact from a Meta lead. The lead's contact fields seed the form,
 * and the saved contact records the lead as its source, which creates no CRM
 * lead and no owner link.
 */
const LeadContactForm = ({ lead, leadId, onSaved }) => {
  const configsQuery = useCustomFieldConfigs(MODEL_TYPE);
  const groupsQuery = useCustomFieldGroups(MODEL_TYPE);

  const preset = useMemo(
    () => ({
      leadSource: "meta",
      leadSourceRecordId: leadId,
      fullName: lead?.name ?? "",
      email: lead?.email ?? "",
      phone: lead?.phone ?? "",
    }),
    [lead, leadId]
  );

  if (configsQuery.isError) {
    return (
      <p className="tw:text-sm tw:text-(--color-text-muted)">
        Could not load the form. Please try again.
      </p>
    );
  }

  if (!lead || configsQuery.isPending || groupsQuery.isPending) {
    return <FormSkeleton sections={3} className="tw:max-w-7xl" />;
  }

  return (
    <ContactForm
      mode={FORM_MODES.create}
      preset={preset}
      customFieldConfigs={configsQuery.data ?? []}
      customFieldGroups={groupsQuery.data ?? []}
      onSaved={onSaved}
    />
  );
};

LeadContactForm.propTypes = {
  lead: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
  leadId: PropTypes.string.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default LeadContactForm;
